import { useContext, useEffect, useMemo } from 'react'
import { ATContext } from './AdobeTargetProvider'

/**
 * Hook to integrate with Adobe Target functionality.
 * Automatically registers the specified target location(s) with the AdobeTargetProvider.
 *
 * @param {Object|Array<Object>} targetConfig - Configuration for Adobe Target Activity. Can be a single config or array of configs.
 * @param {string} targetConfig.location - The name of the location/mbox to request. Must match the activity setup.
 * @param {string[]} [targetConfig.experienceIds] - List of experiment IDs to filter and activate.
 * @returns {Object} Object containing:
 *   @returns {Object} data - Target activity data for the specified location(s). For single config: returns a single object.
 *                            For multiple configs: returns an object with locations as keys.
 *   @returns {boolean} loading - True while Adobe Target is loading or fetching target location offers.
 *   @returns {Error|undefined} error - Error object if request failed or timed out, undefined otherwise.
 * @throws {Error} Throws an error if used outside of an AdobeTargetProvider context.
 */
export default function useAdobeTarget(targetConfig) {
  const context = useContext(ATContext)

  // Validate context first, before accessing any properties
  if (
    !context ||
    typeof context.registerExperiments === 'undefined' ||
    typeof context.getOfferResponses === 'undefined'
  ) {
    throw new Error('useAdobeTarget must be used within an AdobeTargetProvider')
  }

  const { getOfferResponses, loading, errors, registerExperiments } = context

  // Get only relevant errors (global or for this specific activity)
  const error = useMemo(() => {
    if (!errors) return undefined

    // Always show global errors
    if (errors.global) return errors.global

    // For single config, check if there's an error for this location
    if (!Array.isArray(targetConfig) && targetConfig?.location) {
      return errors[targetConfig.location]
    }

    // For array configs, return first error found for any of our locations
    if (Array.isArray(targetConfig)) {
      const locationWithError = targetConfig
        .map((config) => config?.location)
        .filter(Boolean)
        .find((location) => errors[location])

      return locationWithError ? errors[locationWithError] : undefined
    }

    return undefined
  }, [errors, targetConfig])

  // Handle both single config and array of configs
  const configs = useMemo(() => {
    return Array.isArray(targetConfig) ? targetConfig : [targetConfig]
  }, [targetConfig])

  // Register all experiment configs with the provider
  useEffect(() => {
    if (targetConfig) {
      registerExperiments(targetConfig)
    }
  }, [targetConfig, registerExperiments])

  // Create result data object from getOfferResponses
  const data = useMemo(() => {
    if (!targetConfig || !getOfferResponses) {
      return {}
    }

    // Check if any of our locations have errors
    const locationWithData = configs
      .map((config) => config?.location)
      .filter(Boolean)
      .find((location) => getOfferResponses[location])

    if (!locationWithData) {
      return {}
    }

    // If single config, return just that location's data
    if (!Array.isArray(targetConfig)) {
      const { location } = targetConfig
      return getOfferResponses[location] || {}
    }

    // For array of configs, return an object with all matching locations
    return configs.reduce((result, config) => {
      if (config?.location && getOfferResponses[config.location]) {
        result[config.location] = getOfferResponses[config.location]
      }
      return result
    }, {})
  }, [targetConfig, getOfferResponses, configs])

  // Always return the same structure
  return { data, loading, error }
}
